import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/workbench/common/views';
import { VIEWLET_ID } from 'vscode/vscode/vs/workbench/contrib/debug/common/debug';
import { NOTEBOOK_VARIABLE_VIEW_ENABLED } from './notebookVariableContextKeys.js';
import { NotebookVariablesView } from './notebookVariablesView.js';
import { getNotebookEditorFromEditorPane } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { variablesViewIcon } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookIcons';
import { NotebookSetting } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { INotebookExecutionStateService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookExecutionStateService.service';
import { INotebookKernelService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookKernelService.service';
import { INotebookService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookService.service';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';

const _moduleId = "vs/workbench/contrib/notebook/browser/contrib/notebookVariables/notebookVariables";
let NotebookVariables = class NotebookVariables extends Disposable {
    constructor(contextKeyService, configurationService, editorService, notebookExecutionStateService, notebookKernelService, notebookDocumentService) {
        super();
        this.configurationService = configurationService;
        this.editorService = editorService;
        this.notebookExecutionStateService = notebookExecutionStateService;
        this.notebookKernelService = notebookKernelService;
        this.notebookDocumentService = notebookDocumentService;
        this.listeners = [];
        this.initialized = false;
        this.viewEnabled = NOTEBOOK_VARIABLE_VIEW_ENABLED.bindTo(contextKeyService);
        this.listeners.push(this.editorService.onDidActiveEditorChange(() => this.handleInitEvent()));
        this.listeners.push(this.notebookExecutionStateService.onDidChangeExecution((e) => this.handleInitEvent(e.notebook)));
        this.configListener = configurationService.onDidChangeConfiguration((e) => this.handleConfigChange(e));
    }
    handleConfigChange(e) {
        if (e.affectsConfiguration(NotebookSetting.notebookVariablesView)) {
            if (!this.configurationService.getValue(NotebookSetting.notebookVariablesView)) {
                this.viewEnabled.set(false);
            }
            else if (this.initialized) {
                this.viewEnabled.set(true);
            }
            else {
                this.handleInitEvent();
            }
        }
    }
    handleInitEvent(notebook) {
        if (this.configurationService.getValue(NotebookSetting.notebookVariablesView)
            && (!!notebook || this.editorService.activeEditorPane?.getId() === 'workbench.editor.notebook')) {
            if (this.hasVariableProvider(notebook) && !this.initialized && this.initializeView()) {
                this.viewEnabled.set(true);
                this.initialized = true;
                this.listeners.forEach(listener => listener.dispose());
            }
        }
    }
    hasVariableProvider(notebookUri) {
        const notebook = notebookUri ?
            this.notebookDocumentService.getNotebookTextModel(notebookUri) :
            getNotebookEditorFromEditorPane(this.editorService.activeEditorPane)?.getViewModel()?.notebookDocument;
        return notebook && this.notebookKernelService.getMatchingKernel(notebook).selected?.hasVariableProvider;
    }
    initializeView() {
        const debugViewContainer = ( (Registry.as('workbench.registry.view.containers'))).get(VIEWLET_ID);
        if (debugViewContainer) {
            const viewsRegistry = ( (Registry.as(Extensions.ViewsRegistry)));
            const viewDescriptor = {
                id: 'NOTEBOOK_VARIABLES', name: ( localize2WithPath(_moduleId, 0, "Notebook Variables")),
                containerIcon: variablesViewIcon, ctorDescriptor: ( (new SyncDescriptor(NotebookVariablesView))),
                order: 50, weight: 5, canToggleVisibility: true, canMoveView: true, collapsed: true, when: NOTEBOOK_VARIABLE_VIEW_ENABLED,
            };
            viewsRegistry.registerViews([viewDescriptor], debugViewContainer);
            return true;
        }
        return false;
    }
    dispose() {
        super.dispose();
        this.listeners.forEach(listener => listener.dispose());
        this.configListener.dispose();
    }
};
NotebookVariables = ( (__decorate([
    ( (__param(0, IContextKeyService))),
    ( (__param(1, IConfigurationService))),
    ( (__param(2, IEditorService))),
    ( (__param(3, INotebookExecutionStateService))),
    ( (__param(4, INotebookKernelService))),
    ( (__param(5, INotebookService)))
], NotebookVariables)));

export { NotebookVariables };
